import type React from "react";
import { useNavigate } from "react-router";

import type { MassMessageCreatedEntityDto } from "@/api/types";
import ImagePostbox from "@/assets/images/postbox.png";
import { Button } from "@/components/Button/Button";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { isDefined } from "@/helpers/util";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

type CreateMassPostSuccessModalProps = ModalBaseProps & {
  audienceReached: MassMessageCreatedEntityDto | undefined;
};

export function CreateMassPostSuccessModal({
  audienceReached,
  isOpened,
  onOpenChange,
}: CreateMassPostSuccessModalProps): React.ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isAnyResidentReached =
    isDefined(audienceReached?.audienceResidentsCount) && audienceReached.audienceResidentsCount > 0;

  return (
    <Modal.Root title={t("page.admin-create-post.success-modal.title")} {...{ isOpened, onOpenChange }}>
      <div className="flex w-full flex-col items-center justify-center gap-4">
        <img alt="Posted" className="py-8" src={ImagePostbox} width={96} height={96} />
        {isAnyResidentReached && (
          <div className="flex flex-col items-center gap-1 text-center text-body">
            <span>{t("page.admin-create-post.success-modal.audience-reached.description.delivered-to")}</span>
            {/* Amount of projects */}
            {audienceReached.projectCount > 0 && (
              <span className="text-body-bold">
                {`${audienceReached.projectCount} ${t("common.entity.project", {
                  count: audienceReached.projectCount,
                })}`}
              </span>
            )}
            {/* Amount of addresses */}
            {isDefined(audienceReached.addressCount) && audienceReached.addressCount > 0 && (
              <span className="text-body-bold">
                {`${audienceReached.addressCount} ${t("common.entity.address", {
                  count: audienceReached.addressCount,
                })}`}
              </span>
            )}
            {/* Amount of residents */}
            {audienceReached.audienceResidentsCount > 0 ? (
              <span className="text-body-bold">
                {`${audienceReached.audienceResidentsCount} ${t("common.entity.resident", {
                  count: audienceReached.audienceResidentsCount,
                })}`}
              </span>
            ) : (
              <span className="text-body">
                {t("page.admin-create-post.success-modal.audience-reached.description.residents.none")}
              </span>
            )}
          </div>
        )}
      </div>
      <Modal.Actions>
        <Modal.Close>
          <Button styling="tertiary"> {t("page.admin-create-post.success-modal.button.another-message")}</Button>
        </Modal.Close>
        <Button styling="primary" onClick={() => void navigate(routes.portfolio.overview())}>
          {t("page.create-mass-message.success-modal.button.done")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
